/* eslint-disable @next/next/no-img-element */
import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useModalPaystackPayment } from "@src/hooks/useModalPaystackPayment";
import { PaystackProps } from "react-paystack/dist/types";
import { Currency, PaymentProcessor } from "@src/@types/api.d";
import {
  GetPackagePurchaser,
  GetPackageAttires,
  GetPackageOrders,
  GetPackages,
} from "@src/graphql/queries/packages.queries";
import {
  PackageStripeIntent,
  VerifyPackagePayment,
} from "@src/graphql/mutations/packages.mutations";
import {
  usePackageCheckout,
  CheckoutStorage,
} from "@src/hooks/usePackageCheckout";
import {
  checkoutMessage,
  money,
  packageCount,
  packageError,
  unitLabel,
} from "@src/utils/asoebi-packages";
import Button from "../Button";

const storage: CheckoutStorage = {
  getItem: async (key) => sessionStorage.getItem(key),
  setItem: async (key, value) => sessionStorage.setItem(key, value),
  removeItem: async (key) => sessionStorage.removeItem(key),
};
const inputClass =
  "mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-3 text-sm focus:border-[#4e7e77] focus:outline-none disabled:bg-gray-50";

type Props = {
  eventId: string;
  groupId: string;
  groupName: string;
  currency: string;
  deadline: string;
  onBack: () => void;
};
export default function PackageShop(props: Props) {
  const { data, loading, error, refetch } = useQuery(GetPackages, {
    variables: { eventId: props.eventId, groupId: props.groupId },
    fetchPolicy: "cache-and-network",
  });
  const { data: profile } = useQuery(GetPackagePurchaser);
  const { data: attireData } = useQuery(GetPackageAttires, {
    variables: { eventId: props.eventId, groupId: props.groupId },
  });
  const packages = (data?.getAseobiPackages ?? []).filter(
    (p) => p.status === "PUBLISHED"
  );
  const attires = (attireData?.getAseobiAttires ?? []).filter(
    (i) =>
      i.active &&
      i.vendor === "COUPLE" &&
      i.currency_iso.toLowerCase() === props.currency.toLowerCase()
  );
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [yards, setYards] = useState<Record<string, number>>({});
  const [delivery, setDelivery] = useState({
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip: "",
  });
  const [history, setHistory] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const paymentInFlight = useRef(false);
  const [stripeSecret, setStripeSecret] = useState<string | null>(null);
  const [paystackConfig, setPaystackConfig] = useState<PaystackProps | null>(
    null
  );
  const checkout = usePackageCheckout(
    props.eventId,
    props.groupId,
    profile?.user?.uuid ?? "",
    storage
  );
  const [intent] = useMutation(PackageStripeIntent);
  const [verify] = useMutation(VerifyPackagePayment);
  const stripe = useMemo(
    () =>
      process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
        ? loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY)
        : null,
    []
  );
  useModalPaystackPayment(paystackConfig, {
    onSuccess: () => {
      setPaystackConfig(null);
      void confirmPayment(PaymentProcessor.Paystack);
    },
    onClose: () => {
      setPaystackConfig(null);
      void checkout.check().catch(() => {});
    },
    onError: (error) => {
      setPaystackConfig(null);
      checkout.setError(packageError(error));
    },
  });
  useEffect(() => {
    if (checkout.terminal) setStripeSecret(null);
  }, [checkout.terminal]);
  const selected = packages.filter((p) => (counts[p.id] ?? 0) > 0);
  const supported = ["ngn", "usd", "gbp"].includes(
    props.currency.toLowerCase()
  );
  const deadlinePassed = Date.parse(props.deadline) <= Date.now();
  const estimated =
    selected.reduce((sum, p) => sum + (p.price ?? 0) * counts[p.id], 0) +
    attires.reduce((sum, a) => sum + a.price_per_yard * (yards[a.id] ?? 0), 0);
  async function confirmPayment(
    provider: PaymentProcessor,
    reference?: string
  ) {
    if (!checkout.quote) return;
    setPaymentBusy(true);
    try {
      await verify({
        variables: {
          reference: reference || checkout.quote.payment_reference,
          provider,
          subAccountId:
            provider === PaymentProcessor.Stripe
              ? checkout.quote.aseobi_asset.sub_account_id
              : undefined,
        },
      });
    } catch (e) {
      checkout.setError(packageError(e));
    } finally {
      await checkout.check().catch(() => {});
      setPaymentBusy(false);
    }
  }
  async function pay() {
    const quote = checkout.quote;
    if (
      !quote ||
      checkout.expired ||
      checkout.terminal ||
      paymentBusy ||
      !!paystackConfig ||
      paymentInFlight.current
    )
      return;
    paymentInFlight.current = true;
    checkout.setError("");
    setPaymentBusy(true);
    try {
      const status = await checkout.check();
      if (status.data?.getAsoebiCheckoutStatus.status !== "PENDING") return;
      if (quote.currency === Currency.Ngn) {
        const key = process.env.NEXT_PUBLIC_PAYSTACK_PUBLIC_KEY;
        if (!key)
          throw new Error(
            "Payments are temporarily unavailable. Your reservation is saved."
          );
        setPaystackConfig({
          publicKey: key,
          reference: quote.payment_reference,
          email: quote.contact_email,
          amount: Math.round(quote.total_payment_intent_amount * 100),
          currency: "NGN",
          metadata: {
            custom_fields: [],
            checkout_id: quote.checkout_id,
            payment_reference: quote.payment_reference,
          },
          split: {
            type: "percentage",
            bearer_type: "all-proportional",
            subaccounts: quote.split_payments.map((s) => ({
              subaccount: s.sub_account_code,
              share: s.split_percentage,
            })),
          },
        });
      } else {
        if (!stripe)
          throw new Error(
            "Card payments are temporarily unavailable. Your reservation is saved."
          );
        const result = await intent({
          variables: {
            args: {
              customer_email: quote.contact_email,
              event_id: props.eventId,
              inawo_payment_reference: quote.payment_reference,
            },
          },
        });
        const secret = result.data?.initiateStripePaymentIntent.client_secret;
        if (!secret)
          throw new Error("Unable to open card payment. Please try again.");
        setStripeSecret(secret);
      }
    } catch (e) {
      checkout.setError(packageError(e));
    } finally {
      paymentInFlight.current = false;
      setPaymentBusy(false);
    }
  }
  async function prepare(e: React.FormEvent) {
    e.preventDefault();
    if (!selected.length || deadlinePassed || !supported) return;
    if (!delivery.phone.trim()) {
      checkout.setError("Enter your phone number.");
      return;
    }
    const provider =
      props.currency.toLowerCase() === "ngn"
        ? PaymentProcessor.Paystack
        : PaymentProcessor.Stripe;
    await checkout.prepare({
      aseobi_id: props.groupId,
      user_id: profile?.user?.uuid ?? "",
      contact_email: delivery.email || profile?.user?.email || "",
      contact_phone: delivery.phone,
      delivery_address: delivery.address,
      delivery_city: delivery.city,
      delivery_state: delivery.state,
      delivery_zip_code: delivery.zip,
      currency: props.currency.toLowerCase() as Currency,
      payment_provider: provider,
      attires: attires
        .filter((a) => (yards[a.id] ?? 0) > 0)
        .map((a) => ({
          aseobi_id: props.groupId,
          attire_id: a.id,
          quantity: 1,
          yards: yards[a.id],
        })),
      packages: selected.map((p) => ({
        package_id: p.id,
        package_version: p.version,
        quantity: counts[p.id],
      })),
    });
  }
  const busy =
    checkout.loading || checkout.cancelling || paymentBusy || !!paystackConfig;
  return (
    <div className="space-y-6 font-inter">
      <div className="flex items-center justify-between gap-3">
        <button
          className="text-sm text-[#3B5F59] underline"
          onClick={props.onBack}
        >
          ← Back to asoebi
        </button>
        <button
          className="text-sm text-[#3B5F59] underline"
          onClick={() => setHistory((h) => !h)}
        >
          {history ? "Shop packages" : "My orders"}
        </button>
      </div>
      <div>
        <h2 className="text-2xl font-semibold">{props.groupName}</h2>
        <p className="mt-2 text-sm text-gray-500">
          Complete looks, thoughtfully put together. Each package is sold as one
          set.
        </p>
      </div>
      {history ? (
        <GuestPackageOrders eventId={props.eventId} groupId={props.groupId} />
      ) : (
        <>
          {checkout.error && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
            >
              {checkout.error}
              {!checkout.quote && (
                <button
                  type="button"
                  className="mt-2 block underline"
                  onClick={() => void refetch()}
                >
                  Refresh package details
                </button>
              )}
            </div>
          )}
          {!checkout.restored ? (
            <p className="py-6 text-sm text-gray-500">
              Checking for an existing reservation…
            </p>
          ) : checkout.quote ? (
            <div className="space-y-5">
              <div
                className="rounded-xl border border-[#d8e5e2] bg-[#f0f5f4] p-4"
                role="status"
              >
                <h3 className="font-semibold">
                  {checkout.status === "PAID"
                    ? "Order confirmed"
                    : checkout.expired
                    ? "Reservation ended"
                    : "Your package reservation"}
                </h3>
                <p className="mt-2 text-sm">
                  {checkoutMessage(
                    checkout.expired && checkout.status === "PENDING"
                      ? "EXPIRED"
                      : checkout.status
                  )}
                </p>
                {!checkout.terminal && !checkout.expired && (
                  <p className="mt-2 text-sm font-medium">
                    Reserved for {Math.floor(checkout.remainingSeconds / 60)}:
                    {String(checkout.remainingSeconds % 60).padStart(2, "0")}
                  </p>
                )}
              </div>
              <div className="divide-y rounded-xl border px-4">
                {checkout.quote.packages.map((p) => (
                  <div key={p.line_id} className="py-4">
                    <div className="flex justify-between gap-3">
                      <h4 className="font-medium">
                        {p.name} × {p.quantity}
                      </h4>
                      <span>{money(p.total, p.currency)}</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Includes {money(p.fee_amount, p.currency)} service fee
                    </p>
                    <ul className="mt-3 space-y-1 text-sm text-gray-600">
                      {p.components.map((i) => (
                        <li key={i.id}>
                          {Math.round(i.quantity * p.quantity * 100) / 100}{" "}
                          {unitLabel(i.unit, i.quantity * p.quantity)} ·{" "}
                          {i.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {checkout.quote.attires.map((a) => (
                  <p key={a.attire_id} className="py-3 text-sm">
                    Fabric · {a.size} {a.size_unit} × {a.quantity} —{" "}
                    {money(a.total_price, checkout.quote!.currency)}
                  </p>
                ))}
                <div className="space-y-2 py-4 text-sm">
                  {[
                    ["Delivery", checkout.quote.delivery_fee],
                    ["VAT", checkout.quote.vat],
                    ["Late fee", checkout.quote.late_fee],
                    ["Transaction charge", checkout.quote.transaction_charge],
                  ].map(([label, amount]) => (
                    <div key={label} className="flex justify-between">
                      <span>{label}</span>
                      <span>
                        {money(Number(amount), checkout.quote!.currency)}
                      </span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2 text-lg font-semibold">
                    <span>Total to pay</span>
                    <span>
                      {money(
                        checkout.quote.total_payment_intent_amount,
                        checkout.quote.currency
                      )}
                    </span>
                  </div>
                </div>
              </div>
              {stripeSecret &&
              stripe &&
              !checkout.expired &&
              !checkout.terminal ? (
                <Elements
                  stripe={stripe}
                  options={{ clientSecret: stripeSecret }}
                >
                  <PackageStripePayment
                    onPaid={(reference) =>
                      confirmPayment(PaymentProcessor.Stripe, reference)
                    }
                    onError={checkout.setError}
                  />
                </Elements>
              ) : (
                !checkout.terminal &&
                !checkout.expired &&
                checkout.status === "PENDING" && (
                  <Button
                    className="w-full rounded-lg bg-[#4e7e77] px-4 py-3 font-inter text-sm text-white transition hover:bg-[#34544F] disabled:cursor-not-allowed disabled:bg-gray-300"
                    type="button"
                    label="Pay for packages"
                    loading={busy}
                    onClick={() => void pay()}
                  >
                    Pay{" "}
                    {money(
                      checkout.quote.total_payment_intent_amount,
                      checkout.quote.currency
                    )}
                  </Button>
                )
              )}
              <button
                className="block text-sm text-[#3B5F59] underline"
                disabled={busy}
                onClick={() =>
                  void checkout
                    .check()
                    .catch((e) => checkout.setError(packageError(e)))
                }
              >
                Check payment status
              </button>
              {!checkout.terminal &&
                (confirmCancel ? (
                  <div className="rounded-lg border p-4 text-sm">
                    <p>
                      Only cancel if you have not completed payment. If you have
                      paid, check your payment status instead.
                    </p>
                    <div className="mt-3 flex gap-4">
                      <button
                        className="text-red-700 underline"
                        disabled={busy}
                        onClick={() =>
                          void checkout.abandon().then(() => {
                            setConfirmCancel(false);
                            setStripeSecret(null);
                            void refetch();
                          })
                        }
                      >
                        Cancel reservation
                      </button>
                      <button
                        className="underline"
                        onClick={() => setConfirmCancel(false)}
                      >
                        Keep reservation
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    className="text-sm text-gray-500 underline"
                    disabled={busy}
                    onClick={() => setConfirmCancel(true)}
                  >
                    Cancel reservation and change selection
                  </button>
                ))}
              {checkout.status === "PAID" && (
                <Button
                  className="w-full rounded-lg bg-[#4e7e77] px-4 py-3 font-inter text-sm text-white transition hover:bg-[#34544F] disabled:cursor-not-allowed disabled:bg-gray-300"
                  type="button"
                  label="Back to packages"
                  onClick={() =>
                    void checkout.finish().then(() => {
                      setCounts({});
                      setYards({});
                      void refetch();
                    })
                  }
                >
                  Back to packages
                </Button>
              )}
              <p className="break-all text-xs text-gray-400">
                Reference: {checkout.quote.payment_reference}
              </p>
            </div>
          ) : checkout.locked ? (
            <div className="space-y-4 rounded-xl border p-4">
              <p className="text-sm text-gray-600">
                An earlier checkout request needs to be recovered before you
                change your selection.
              </p>
              <Button
                className="w-full rounded-lg bg-[#4e7e77] px-4 py-3 font-inter text-sm text-white transition hover:bg-[#34544F] disabled:cursor-not-allowed disabled:bg-gray-300"
                type="button"
                label="Recover checkout"
                loading={busy}
                onClick={() => void checkout.recover()}
              >
                Recover checkout
              </Button>
            </div>
          ) : (
            <form onSubmit={prepare} className="space-y-6">
              {deadlinePassed && (
                <p role="alert" className="rounded-lg bg-amber-50 p-3 text-sm">
                  The purchase deadline has passed.
                </p>
              )}
              {!supported && (
                <p className="text-sm text-gray-500">
                  Packages are not available in this group's currency.
                </p>
              )}
              {loading && !data ? (
                <div className="h-40 animate-pulse rounded-xl bg-gray-100" />
              ) : error ? (
                <p role="alert">
                  {packageError(error)}{" "}
                  <button
                    type="button"
                    className="underline"
                    onClick={() => void refetch()}
                  >
                    Try again
                  </button>
                </p>
              ) : !packages.length ? (
                <p className="rounded-xl border border-dashed p-6 text-sm text-gray-500">
                  No packages are on sale in this group yet.
                </p>
              ) : (
                packages.map((p) => (
                  <article
                    key={p.id}
                    className="overflow-hidden rounded-xl border border-gray-200"
                  >
                    {p.cover_image && (
                      <img
                        src={p.cover_image}
                        alt={p.name}
                        className="h-48 w-full object-cover"
                      />
                    )}
                    <div className="space-y-3 p-4">
                      <div className="flex justify-between gap-3">
                        <h3 className="text-lg font-semibold">{p.name}</h3>
                        <span className="h-fit shrink-0 whitespace-nowrap rounded-full bg-[#f0f5f4] px-2 py-1 text-xs text-[#3B5F59]">
                          {p.sold_out
                            ? "Sold out"
                            : `${p.available_quantity} available`}
                        </span>
                      </div>
                      {p.images.filter((src) => src !== p.cover_image).length >
                        0 && (
                        <div className="flex gap-2 overflow-x-auto">
                          {p.images
                            .filter((src) => src !== p.cover_image)
                            .map((src) => (
                              <a
                                key={src}
                                href={src}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <img
                                  src={src}
                                  alt={p.name}
                                  className="h-16 w-16 rounded-lg object-cover"
                                />
                              </a>
                            ))}
                        </div>
                      )}
                      <p className="text-sm text-gray-500">{p.description}</p>
                      <p className="font-semibold">
                        {money(p.price ?? 0, p.currency_iso)}{" "}
                        <span className="text-xs font-normal text-gray-500">
                          per package, before service fees
                        </span>
                      </p>
                      <details>
                        <summary className="cursor-pointer text-sm font-medium text-[#3B5F59]">
                          What's included · {p.items.length} items
                        </summary>
                        <div className="mt-3 space-y-3">
                          {p.items.map((i) => (
                            <div
                              key={i.id}
                              className="flex gap-3 border-t pt-3"
                            >
                              {i.images[0] && (
                                <img
                                  src={i.images[0]}
                                  alt={i.name}
                                  className="h-14 w-14 rounded-lg object-cover"
                                />
                              )}
                              <div>
                                <p className="text-sm font-medium">
                                  {i.quantity} {unitLabel(i.unit, i.quantity)} ·{" "}
                                  {i.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {i.description}
                                </p>
                                {i.images.length > 1 && (
                                  <div className="mt-2 flex gap-2">
                                    {i.images.slice(1).map((src) => (
                                      <a
                                        href={src}
                                        key={src}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        <img
                                          src={src}
                                          alt={i.name}
                                          className="h-12 w-12 rounded object-cover"
                                        />
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                      <div className="flex items-center justify-between border-t pt-3">
                        <span className="text-xs text-gray-500">
                          Whole packages only
                        </span>
                        <Quantity
                          value={counts[p.id] ?? 0}
                          max={
                            p.sold_out || deadlinePassed
                              ? 0
                              : Math.min(1000, p.available_quantity)
                          }
                          disabled={checkout.locked}
                          name={p.name}
                          onChange={(n) =>
                            setCounts((c) => ({
                              ...c,
                              [p.id]: packageCount(n, p.available_quantity),
                            }))
                          }
                        />
                      </div>
                    </div>
                  </article>
                ))
              )}
              {packages.length > 0 && attires.length > 0 && (
                <details className="rounded-xl border p-4">
                  <summary className="cursor-pointer text-sm font-medium">
                    Add standalone fabric to your package order
                  </summary>
                  <p className="mt-2 text-xs text-gray-500">
                    Couple-supplied fabrics only. Oyple items use a separate
                    checkout.
                  </p>
                  {attires.map((a) => (
                    <div
                      className="mt-4 flex items-center justify-between gap-3"
                      key={a.id}
                    >
                      <div className="text-sm">
                        <p>{a.name}</p>
                        <p className="text-xs text-gray-500">
                          {money(a.price_per_yard, a.currency_iso)} / yard
                          before fees
                        </p>
                      </div>
                      <Quantity
                        name={a.name}
                        value={yards[a.id] ?? 0}
                        max={
                          a.yards_available < Math.max(1, a.minimum_yards ?? 1)
                            ? 0
                            : a.yards_available
                        }
                        disabled={checkout.locked}
                        onChange={(n) => {
                          const min = Math.max(1, a.minimum_yards ?? 1);
                          const current = yards[a.id] ?? 0;
                          setYards((y) => ({
                            ...y,
                            [a.id]:
                              n > current
                                ? Math.min(
                                    a.yards_available,
                                    current === 0 ? min : n
                                  )
                                : n < min
                                ? 0
                                : n,
                          }));
                        }}
                      />
                    </div>
                  ))}
                </details>
              )}
              <fieldset
                disabled={checkout.locked || busy}
                className="space-y-4"
              >
                <h3 className="font-semibold">Delivery details</h3>
                {[
                  ["email", "Email address", "email"],
                  ["phone", "Phone number", "tel"],
                  ["address", "Delivery address", "text"],
                  ["city", "City", "text"],
                  ["state", "State / region", "text"],
                  ["zip", "Postal code (optional)", "text"],
                ].map(([key, label, type]) => (
                  <label key={key} className="block text-sm">
                    {label}
                    <input
                      type={type}
                      className={inputClass}
                      required={key !== "zip"}
                      value={
                        delivery[key as keyof typeof delivery] ||
                        (key === "email" ? profile?.user?.email ?? "" : "")
                      }
                      onChange={(e) =>
                        setDelivery((d) => ({ ...d, [key]: e.target.value }))
                      }
                    />
                  </label>
                ))}
              </fieldset>
              {checkout.locked ? (
                <p className="text-sm text-gray-600">
                  Retry your checkout request to recover its reservation.
                </p>
              ) : (
                <div className="flex justify-between border-t pt-4 text-sm">
                  <span>Subtotal before fees</span>
                  <strong>{money(estimated, props.currency)}</strong>
                </div>
              )}
              <Button
                className="w-full rounded-lg bg-[#4e7e77] px-4 py-3 font-inter text-sm text-white transition hover:bg-[#34544F] disabled:cursor-not-allowed disabled:bg-gray-300"
                type="submit"
                label="Review package order"
                loading={busy}
                disabled={
                  !selected.length ||
                  deadlinePassed ||
                  !supported ||
                  !profile?.user?.uuid
                }
              >
                Review order & reserve packages
              </Button>
              {!profile?.user?.uuid && (
                <p className="text-sm text-gray-500">
                  Sign in to purchase packages.
                </p>
              )}
            </form>
          )}
        </>
      )}
    </div>
  );
}
function Quantity({
  value,
  max,
  onChange,
  name,
  disabled,
}: {
  value: number;
  max: number;
  onChange: (n: number) => void;
  name: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center rounded-lg border">
      <button
        type="button"
        className="h-10 w-10 disabled:opacity-30"
        aria-label={`Remove ${name}`}
        disabled={disabled || value <= 0}
        onClick={() => onChange(value - 1)}
      >
        −
      </button>
      <span className="min-w-[28px] text-center text-sm" aria-live="polite">
        {value}
      </span>
      <button
        type="button"
        className="h-10 w-10 disabled:opacity-30"
        aria-label={`Add ${name}`}
        disabled={disabled || value >= max}
        onClick={() => onChange(value + 1)}
      >
        +
      </button>
    </div>
  );
}
function PackageStripePayment({
  onPaid,
  onError,
}: {
  onPaid: (reference: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  return (
    <form
      className="space-y-4"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setBusy(true);
        try {
          const result = await stripe.confirmPayment({
            elements,
            confirmParams: { return_url: window.location.href },
            redirect: "if_required",
          });
          if (result.error)
            onError(result.error.message || "Unable to complete payment");
          else if (result.paymentIntent) await onPaid(result.paymentIntent.id);
        } catch (err) {
          onError(packageError(err));
        } finally {
          setBusy(false);
        }
      }}
    >
      <PaymentElement />
      <Button
        className="w-full rounded-lg bg-[#4e7e77] px-4 py-3 font-inter text-sm text-white transition hover:bg-[#34544F] disabled:cursor-not-allowed disabled:bg-gray-300"
        type="submit"
        label="Confirm card payment"
        loading={busy}
        disabled={!stripe || !elements}
      >
        Confirm payment
      </Button>
    </form>
  );
}
export function GuestPackageOrders({
  eventId,
  groupId,
}: {
  eventId: string;
  groupId?: string;
}) {
  const { data, loading, error, refetch } = useQuery(GetPackageOrders, {
    variables: { eventId, groupId },
    fetchPolicy: "network-only",
  });
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">My orders</h3>
      {loading ? (
        <p>Loading orders…</p>
      ) : error ? (
        <p role="alert">
          {packageError(error)}{" "}
          <button className="underline" onClick={() => void refetch()}>
            Try again
          </button>
        </p>
      ) : !data?.getAseobiOrdersV2.length ? (
        <p className="text-sm text-gray-500">
          Your orders will appear here after payment.
        </p>
      ) : (
        data.getAseobiOrdersV2.map((o) => (
          <article
            key={o.reference}
            className="space-y-3 rounded-xl border p-4"
          >
            <div className="flex justify-between gap-3">
              <p className="text-sm">
                {new Date(o.created_at).toLocaleDateString()}
              </p>
              <strong>
                {o.currency
                  ? money(o.payment_total, o.currency)
                  : o.payment_total.toLocaleString()}
              </strong>
            </div>
            <p className="text-sm">
              {o.payment_status === "PAID"
                ? "Payment received"
                : "Payment received · awaiting resolution"}
            </p>
            {o.lines.map((l) => (
              <div key={l.id} className="border-t pt-3 text-sm">
                <p className="font-medium">
                  {l.package
                    ? `${l.package.name} × ${l.quantity}`
                    : `Fabric · ${l.attire?.yards} ${l.attire?.size_unit}`}
                </p>
                {l.vendor === "OYPLE" && (
                  <p className="text-xs text-gray-500">
                    Previously supplied by Oyple
                  </p>
                )}
                {l.package?.component_totals.map((i) => (
                  <p
                    className="mt-1 text-xs text-gray-500"
                    key={i.component_id}
                  >
                    {i.quantity} {unitLabel(i.unit, i.quantity)} · {i.name}
                  </p>
                ))}
                <p className="mt-2 text-xs text-[#3B5F59]">
                  {o.payment_status === "PAID"
                    ? l.fulfilment_status.toLowerCase()
                    : "Awaiting resolution"}
                </p>
              </div>
            ))}
            {o.payment_status !== "PAID" && (
              <p className="text-sm text-amber-900">
                {checkoutMessage(o.payment_status)}
              </p>
            )}
          </article>
        ))
      )}
    </div>
  );
}
